import { Link } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, ChevronUp, Settings, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import type { Category } from "@/hooks/use-categories";
import { useDeleteCategory, useUpdateCategory } from "@/hooks/use-categories";
import { DeactivateEntityDialog } from "./DeactivateEntityDialog";
import { DeleteEntityDialog } from "./DeleteEntityDialog";

type CategoryDialogState = { type: "none" } | { type: "deactivate" } | { type: "delete" };

interface CategoryRowProps {
  category: Category;
  editMode: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  isReordering: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

export function CategoryRow({
  category,
  editMode,
  canMoveUp,
  canMoveDown,
  isReordering,
  onMoveUp,
  onMoveDown,
}: CategoryRowProps) {
  const [catDialog, setCatDialog] = useState<CategoryDialogState>({ type: "none" });

  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();

  function closeCatDialog() {
    setCatDialog({ type: "none" });
  }

  function handleDeactivateConfirm() {
    updateCategory.mutate({ id: category.id, active: false }, { onSuccess: closeCatDialog });
  }

  function handleDeleteConfirm() {
    deleteCategory.mutate(category.id, { onSuccess: closeCatDialog });
  }

  return (
    <>
      <tr className="border-b border-border hover:bg-muted/30 transition-colors">
        <td className="px-4 py-3">
          <Link
            to="/dashboard/settings/category/$categoryId"
            params={{ categoryId: category.id }}
            className="group inline-flex items-center gap-1 font-medium text-foreground hover:text-primary"
          >
            {category.name}
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
          </Link>
          {category.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
              {category.description}
            </p>
          )}
        </td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-1">
            {category.jobTypeCodes.map((code) => (
              <span key={code} className={`chip-jobtype chip-jobtype--${code}`}>
                {code}
              </span>
            ))}
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-muted-foreground">
          {category.activeSubtaskCount}/{category.totalSubtaskCount} active
        </td>
        <td className="px-4 py-3">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              category.active ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground"
            }`}
          >
            {category.active ? "Active" : "Inactive"}
          </span>
        </td>
        {editMode && (
          <td className="px-4 py-3 text-right">
            <div className="flex items-center justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                disabled={!canMoveUp || isReordering}
                onClick={onMoveUp}
                title="Move up"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                disabled={!canMoveDown || isReordering}
                onClick={onMoveDown}
                title="Move down"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" title="Manage" asChild>
                <Link
                  to="/dashboard/settings/category/$categoryId"
                  params={{ categoryId: category.id }}
                >
                  <Settings className="h-4 w-4" />
                </Link>
              </Button>
              {category.active ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={() => setCatDialog({ type: "deactivate" })}
                  title="Deactivate"
                >
                  <span className="text-xs font-medium">Off</span>
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={() => updateCategory.mutate({ id: category.id, active: true })}
                  title="Activate"
                  disabled={updateCategory.isPending}
                >
                  <span className="text-xs font-medium">On</span>
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={() => setCatDialog({ type: "delete" })}
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </td>
        )}
      </tr>

      {catDialog.type === "deactivate" && (
        <DeactivateEntityDialog
          open
          onOpenChange={(open) => !open && closeCatDialog()}
          entityType="category"
          entityId={category.id}
          entityName={category.name}
          isPending={updateCategory.isPending}
          onConfirm={handleDeactivateConfirm}
        />
      )}

      {catDialog.type === "delete" && (
        <DeleteEntityDialog
          open
          onOpenChange={(open) => !open && closeCatDialog()}
          entityType="category"
          entityName={category.name}
          isPending={deleteCategory.isPending}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
}
